/** @jsxImportSource @emotion/react */
import { useGetCountries } from "../../service/api";
import { ActionSpinner } from "../../components/ActionSpinner";
import { europeanCountryFlags } from "../../util";
import { Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CountrySpecificValues, getCountrySpecificValues } from "../../util";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { effects } from "../../components/effects";
import { StyledText } from "../../assets/typography/typography";
import { css } from "@emotion/react";
import { colors } from "../../components/colors";
import { VerticalDivider } from "../../components/VerticalDivider";
import { useHandleFetchError } from "../../error-handler";
import { timezones } from "./OrganizationTopbarSection";

export const localStorageCountrySpecificValues = "countrySpecificValues";
export const vatPercentages = new Map([
  ["NO", 15],
  ["DK", 25],
  ["IS", 11],
  ["GL", 0],
  ["BE", 21],
  ["FR", 20],
  ["SE", 12],
]);
export const locales = new Map([
  ["NO", "nb"],
  ["DK", "da"],
  ["IS", "is"],
  ["GL", "kl"],
  ["NL", "nl"],
  ["SE", "sv"],
  ["FR", "fr"],
]);

const selectItemStyle = css`
  margin-top: 2px;
  min-width: max-content;
  width: 100%;
`;

const wrapperStyle = css`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 5px;
  margin-right: 16px;
`;

type Props = {
  showVerticalDivider?: boolean;
};

export const CountryDropdown = ({ showVerticalDivider }: Props) => {
  const {
    data: countries,
    isLoading,
    error: getCountriesError,
  } = useGetCountries();
  const { countryCode } = getCountrySpecificValues();

  const onCountrySelect = (value: string) => {
    let countrySpecificValues: CountrySpecificValues = {
      countryCode: value,
      currency:
        countries?.find((c) => c.countryCode === value)?.currency || "DKK",
      vatPercentage: vatPercentages.get(value) ?? 25,
      locale: locales.get(value) ?? "da",
      timezone: timezones.get(value) ?? "Europe/Copenhagen",
    };
    if (localStorage) {
      localStorage.setItem(
        localStorageCountrySpecificValues,
        JSON.stringify(countrySpecificValues)
      );
    }
  };

  useHandleFetchError(getCountriesError);

  return (
    <div css={wrapperStyle}>
      {isLoading ? (
        <ActionSpinner />
      ) : (
        <Select
          bordered={false}
          suffixIcon={<FontAwesomeIcon icon={solid("chevron-down")} />}
          defaultValue={countryCode}
          placement="bottomRight"
          dropdownStyle={{
            minWidth: "200px",
            boxShadow: effects.popover,
            borderRadius: "4px",
          }}
          optionLabelProp="label"
          onSelect={onCountrySelect}
        >
          {countries
            ?.filter((c) => !!c.enabled)
            .map((c, i) => (
              <Select.Option
                key={i}
                value={c.countryCode}
                label={europeanCountryFlags.get(c.countryCode)}
              >
                <StyledText
                  css={selectItemStyle}
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {europeanCountryFlags.get(c.countryCode)} {c.englishName}
                </StyledText>
              </Select.Option>
            ))}
        </Select>
      )}
      {showVerticalDivider && <VerticalDivider height="16px" />}
    </div>
  );
};
